import { useState, useRef, useEffect, useMemo } from "react";
import {
  motion, AnimatePresence,
  useMotionValue, useSpring, useTransform,
} from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import UserDashboardLayout from "../../layouts/UserDashboardLayout";
import {
  FiPlay, FiClock, FiStar, FiBookmark, FiCheck,
  FiLock, FiSearch, FiFilter, FiChevronRight, FiChevronLeft, FiX,
  FiVolume2, FiMaximize2, FiDownload, FiThumbsUp,
  FiFileText, FiList,  FiTrendingUp, FiEye,
  FiArrowRight, FiShield, FiPlayCircle, FiCheckCircle,
  FiAlertCircle, FiRefreshCw,} from "react-icons/fi";
import { HiSparkles } from "react-icons/hi2";
import Topbar from "../../components/userDashboard/Topbar";
import BrandLoader from "../../components/BrandLoader";
import { practiceVideoAPI, userAPI } from "../../services/api";
import { useDynamicPackages } from "../../hooks/useDynamicPackages";
import { useCheckIn } from "../../context/CheckInContext";

// ─── TOKENS ──────────────────────────────────────────────────────────────────
const T = { orange:"#F29A0C", orange2:"#E97818", olive:"#6B8A5A", olive2:"#8BA47A" };
const glass = {
  background: "rgba(255,255,255,0.82)",
  border: "1px solid rgba(255,255,255,0.95)",
  backdropFilter: "blur(32px)",
  WebkitBackdropFilter: "blur(32px)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.06), inset 0 1px 0 #fff",
};

// ─── TIERS ────────────────────────────────────────────────────────────────────
const TIERS = {
  free:     { label:"Free Trial", rank:0, color:"#888",    bg:"rgba(180,180,180,0.12)", border:"rgba(180,180,180,0.25)", grad:"linear-gradient(135deg,#AAA,#888)" },
  basic:    { label:"Basic",      rank:1, color:T.olive,   bg:"rgba(107,138,90,0.12)",  border:"rgba(107,138,90,0.28)",  grad:`linear-gradient(135deg,${T.olive},${T.olive2})` },
  standard: { label:"Standard",   rank:2, color:T.orange,  bg:"rgba(242,154,12,0.12)",  border:"rgba(242,154,12,0.28)",  grad:`linear-gradient(135deg,${T.orange},${T.orange2})` },
  premium:  { label:"Premium",    rank:3, color:"#C8660A", bg:"rgba(242,154,12,0.18)",  border:"rgba(242,154,12,0.4)",   grad:"linear-gradient(135deg,#F29A0C,#C8660A,#E97818)" },
  pro:      { label:"Pro",        rank:4, color:"#27500A", bg:"rgba(78,104,69,0.18)",   border:"rgba(78,104,69,0.4)",    grad:"linear-gradient(135deg,#4E6845,#6B8A5A,#27500A)" },
};
const TIER_ORDER = ["free","basic","standard","premium","pro"];

const normalizeTier = (t) => {
  if (!t) return "free";
  const low = t.toLowerCase().trim();
  if (low.includes("pro") || low.includes("ytt") || low.includes("vip") || low.includes("diamond")) return "pro";
  if (low.includes("premium") || low.includes("premiun")) return "premium";
  if (low.includes("standard") || low.includes("silver")) return "standard";
  if (low.includes("basic") || low.includes("bronze")) return "basic";
  if (low.includes("free") || low.includes("trial")) return "free";
  return low;
};

const UPGRADE_PERKS = {
  basic:    ["Full foundation video library","Basic weekly diet plan","2 live sessions/month","Email support"],
  standard: ["Intermediate video library","Personalised weekly diet","6 live sessions/month","Chat support"],
  premium:  ["Complete video library","Full nutrition plan","Unlimited live sessions","Priority + downloads"],
  pro:      ["YTT full curriculum + Pro features","Advanced protocols","Dedicated batches","1-on-1 mentoring"],
};

const CATS = ["All","Bookmarked","Asana","Pranayama","Meditation","YTT","Breathwork"];
const CAT_ACCENT = { Asana:T.orange, Pranayama:T.olive, Meditation:"#534AB7", YTT:"#3E5C2E", Breathwork:"#0F6E56" };
const CAT_LIGHT = { Asana:"rgba(242,154,12,0.55)", Pranayama:"rgba(107,138,90,0.55)", Meditation:"rgba(83,74,183,0.55)", YTT:"rgba(62,92,46,0.55)", Breathwork:"rgba(15,110,86,0.55)" };

const CH_NAMES = ["Warm-Up & Centering","Breath Awareness","Standing Sequence","Core Activation","Flow Series","Peak Poses","Counter Poses","Final Relaxation","Integration","Q&A","Practice Review","Closing Pranayama","Savasana","Summary","Cool-Down","Closing"];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const getTierRank = (tierName, packages = []) => {
  if (!tierName) return 0;
  const low = String(tierName).toLowerCase().trim();
  
  // Free Tier
  if (low === "free" || low === "free trial" || low === "trial" || low === "all" || low === "all users") return 0;

  // Canonical standard tiers
  if (low === "basic" || low === "basic plan" || low === "bronze") return 1;
  if (low === "standard" || low === "standard plan" || low === "silver") return 2;
  if (low === "premium" || low === "premium plan") return 3;
  if (low === "pro" || low === "pro plan" || low === "ytt" || low === "ytt batch") return 4;

  if (Array.isArray(packages) && packages.length > 0) {
    // 1. Exact match against DB packages
    const exactPkg = packages.find(p => {
      const nameLow = (p.name || "").toLowerCase().trim();
      const typeLow = (p.packageType || "").toLowerCase().trim();
      return nameLow === low || typeLow === low;
    });
    if (exactPkg && typeof exactPkg.accessLevel === "number" && !isNaN(exactPkg.accessLevel)) {
      return exactPkg.accessLevel;
    }

    // 2. Partial match against custom packages in DB
    const partialPkg = packages.find(p => {
      const nameLow = (p.name || "").toLowerCase().trim();
      const typeLow = (p.packageType || "").toLowerCase().trim();
      return (nameLow && nameLow.includes(low)) || (typeLow && typeLow.includes(low));
    });
    if (partialPkg && typeof partialPkg.accessLevel === "number" && !isNaN(partialPkg.accessLevel)) {
      return partialPkg.accessLevel;
    }
  }

  // 3. General keyword fallbacks
  if (low.includes("pro") || low.includes("ytt") || low.includes("vip")) return 4;
  if (low.includes("premium") || low.includes("gold")) return 3;
  if (low.includes("standard") || low.includes("silver")) return 2;
  if (low.includes("basic") || low.includes("bronze")) return 1;
  return 1;
};

export const canUserAccessContent = (contentTier, userTier, userRank = 0, packages = []) => {
  if (!contentTier) return true;
  const cTier = String(contentTier).toLowerCase().trim();
  if (cTier === "free" || cTier === "free trial" || cTier === "trial" || cTier === "all" || cTier === "all users") return true;

  const uTier = String(userTier || "").toLowerCase().trim();

  // 1. Direct tier match (e.g. user subscribed to Gold watching Gold)
  if (uTier && (uTier === cTier || uTier.includes(cTier) || cTier.includes(uTier))) {
    return true;
  }

  const requiredRank = getTierRank(contentTier, packages);

  // 2. Standard baseline preset tiers
  const isStandardPreset = ["basic", "basic plan", "bronze", "standard", "standard plan", "silver", "premium", "premium plan", "pro", "pro plan", "ytt", "ytt batch"].includes(cTier);

  if (isStandardPreset) {
    return userRank >= requiredRank;
  }

  // 3. For Custom Tiers (e.g. Gold at Level 2):
  // Only higher-tier subscribers (e.g. Premium Level 3, Pro Level 4) unlock lower custom tiers
  return userRank > requiredRank;
};

function TierBadge({ tier, sm }) {
  if (!tier) tier = "Free";
  const low = String(tier).toLowerCase().trim();
  const normKey = normalizeTier(tier);
  const t = TIERS[low] || TIERS[normKey] || {
    label: tier,
    color: low.includes("pro") || low.includes("ytt") ? "#27500A" : T.orange,
    bg: low.includes("pro") || low.includes("ytt") ? "rgba(78,104,69,0.18)" : "rgba(242,154,12,0.12)",
    border: low.includes("pro") || low.includes("ytt") ? "rgba(78,104,69,0.4)" : "rgba(242,154,12,0.28)"
  };
  
  // Preserve exact label (e.g. YTT, Pro, Gold, VIP, Basic)
  const labelText = String(tier).toUpperCase() === "YTT" ? "YTT" : tier;
  return (
    <span style={{ background:t.bg, color:t.color, border:`1px solid ${t.border}`,
      fontSize:sm?9:10, fontWeight:800, padding:sm?"2px 7px":"3px 10px",
      borderRadius:100, letterSpacing:0.2, whiteSpace:"nowrap", fontFamily:"'Inter',sans-serif" }}>
      {labelText}
    </span>
  );
}

function Stars({ rating }) {
  return (
    <span style={{ display:"flex", alignItems:"center", gap:3 }}>
      <FiStar style={{ color:T.orange, fontSize:11, fill:T.orange }} />
      <span style={{ fontSize:11, fontWeight:800, color:T.orange, fontFamily:"'Inter',sans-serif" }}>{rating.toFixed(1)}</span>
    </span>
  );
}

function ProgressRing({ pct, size=32, stroke=3 }) {
  const r = (size-stroke)/2, c = 2*Math.PI*r;
  return (
    <svg width={size} height={size} style={{ transform:"rotate(-90deg)", flexShrink:0 }}>
      <circle cx={size/2} cy={size/2} r={r} stroke="rgba(239,229,216,0.7)" strokeWidth={stroke} fill="none"/>
      <motion.circle cx={size/2} cy={size/2} r={r} stroke={T.orange} strokeWidth={stroke} fill="none"
        strokeLinecap="round" strokeDasharray={c}
        initial={{ strokeDashoffset:c }} animate={{ strokeDashoffset:c-(pct/100)*c }}
        transition={{ duration:1.2, ease:"easeOut" }} />
    </svg>
  );
}

function useTilt(n=7) {
  const ref = useRef(null);
  const mx = useMotionValue(0), my = useMotionValue(0);
  const rx = useSpring(useTransform(my,[-0.5,0.5],[n,-n]),{stiffness:260,damping:26});
  const ry = useSpring(useTransform(mx,[-0.5,0.5],[-n,n]),{stiffness:260,damping:26});
  const handlers = {
    onMouseMove: e => { const r=ref.current?.getBoundingClientRect(); if(!r) return; mx.set((e.clientX-r.left)/r.width-.5); my.set((e.clientY-r.top)/r.height-.5); },
    onMouseLeave: () => { mx.set(0); my.set(0); },
  };
  return { ref, rotateX:rx, rotateY:ry, handlers };
}

// ─── VIDEO CARD (GRID) ────────────────────────────────────────────────────────
function VideoCard({ video, userTier, userRank, packages=[], onPlay, onUpgrade, isBookmarked, onToggleBookmark, onLike, delay=0 }) {
  const [hov, setHov] = useState(false);
  const tilt = useTilt(5);
  const locked = typeof video.isLocked === "boolean" ? video.isLocked : !canUserAccessContent(video.tier, userTier, userRank, packages);
  const acc = CAT_ACCENT[video.cat] || T.orange;
  const accLight = CAT_LIGHT[video.cat] || "rgba(242,154,12,0.5)";

  return (
    <motion.div ref={tilt.ref} {...tilt.handlers}
      style={{ rotateX:tilt.rotateX, rotateY:tilt.rotateY, transformStyle:"preserve-3d", perspective:900 }}
      initial={{ opacity:0, y:28 }} whileInView={{ opacity:1, y:0 }}
      viewport={{ once:true }} transition={{ delay, duration:0.5, ease:[0.22,1,0.36,1] }}
      onHoverStart={() => setHov(true)} onHoverEnd={() => setHov(false)}>
      <motion.div onClick={() => locked ? onUpgrade(video) : onPlay(video)}
        whileHover={{ y:-10 }}
        style={{ borderRadius:26, overflow:"hidden", cursor:"pointer",
          background:"rgba(255,255,255,0.85)", border:"1px solid rgba(255,255,255,0.95)",
          boxShadow:hov?"0 32px 70px rgba(0,0,0,0.12)":"0 14px 44px rgba(0,0,0,0.06)",
          transition:"box-shadow 0.35s" }}>

        {/* Thumb */}
        <div style={{ height:152, position:"relative", overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center",
          background: `linear-gradient(145deg,${accLight},${acc}44)` }}>

          {/* Actual Poster Image */}
          {video.thumb && (
            <img 
              src={video.thumb} 
              alt={video.title} 
              style={{ 
                position: "absolute", 
                inset: 0, 
                width: "100%", 
                height: "100%", 
                objectFit: "cover",
                filter: locked ? "grayscale(40%) contrast(90%) brightness(85%)" : "none",
                transition: "transform 0.4s"
              }} 
            />
          )}

          {/* Background icon */}
          <motion.div animate={!locked ? { scale:[1,1.08,1], opacity:[0.6,0.9,0.6] } : {}}
            transition={{ duration:3.5, repeat:Infinity, ease:"easeInOut" }}
            style={{ position: "relative", zIndex: 2 }}>
            {locked
              ? null
              : <FiPlayCircle style={{ fontSize:54, color:`${acc}66` }} />
            }
          </motion.div>

          {/* Hover play */}
          <AnimatePresence>
            {hov && !locked && (
              <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
                style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.3)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <motion.div initial={{ scale:0.7 }} animate={{ scale:1 }} exit={{ scale:0.7 }}
                  transition={{ type:"spring", stiffness:400, damping:22 }}
                  style={{ width:54, height:54, borderRadius:"50%", background:"rgba(255,255,255,0.95)",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    boxShadow:`0 10px 28px rgba(0,0,0,0.28)` }}>
                  <FiPlay style={{ color:T.orange, fontSize:24, marginLeft:3 }} />
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Lock overlay */}
          {locked && (
            <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.52)", zIndex: 5,
              display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:8 }}>
              <div style={{ width:48, height:48, borderRadius:"50%", background:"rgba(255,255,255,0.14)",
                backdropFilter:"blur(8px)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <FiLock style={{ fontSize:20, color:"#fff" }} />
              </div>
              <p style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.9)" }}>Upgrade to {packages.find(p => (p.packageType || p.name || "").toLowerCase().trim() === (video.tier || "").toLowerCase().trim())?.name || TIERS[video.tier?.toLowerCase()]?.label || video.tier}</p>
              <p style={{ fontSize:10, color:"rgba(255,255,255,0.55)", display:"flex", alignItems:"center", gap:4 }}>
                <FiArrowRight size={9}/> Tap to see plan
              </p>
            </div>
          )}

          {/* Progress bar */}
          {video.pct > 0 && !locked && (
            <div style={{ position:"absolute", bottom:0, left:0, right:0, height:3, background:"rgba(255,255,255,0.2)" }}>
              <motion.div style={{ height:"100%", background:video.pct===100?T.olive:T.orange }}
                initial={{ width:0 }} animate={{ width:`${video.pct}%` }} transition={{ duration:1, delay:delay+0.3 }}/>
            </div>
          )}

          {/* Top-left cat tag */}
          <div style={{ position:"absolute", top:10, left:10,
            background:"rgba(0,0,0,0.45)", backdropFilter:"blur(8px)",
            color:"#fff", fontSize:9, fontWeight:800, padding:"3px 9px", borderRadius:100 }}>
            {video.cat}
          </div>

          {/* Completed checkmark */}
          {video.pct===100 && !locked && (
            <div style={{ position:"absolute", top:10, right:10, width:26, height:26, borderRadius:"50%",
              background:T.olive, display:"flex", alignItems:"center", justifyContent:"center",
              boxShadow:`0 4px 12px ${T.olive}55` }}>
              <FiCheck style={{ color:"#fff", fontSize:12 }} />
            </div>
          )}

          {/* Duration */}
          <div style={{ position:"absolute", bottom:10, right:10,
            background:"rgba(0,0,0,0.55)", backdropFilter:"blur(8px)",
            color:"#fff", fontSize:9, fontWeight:700, padding:"3px 9px", borderRadius:100,
            display:"flex", alignItems:"center", gap:4 }}>
            <FiClock size={8}/> {video.dur}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding:"16px 18px" }}>
          <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8, marginBottom:6 }}>
            <p style={{ fontFamily:"'Poppins',sans-serif", fontSize:13, fontWeight:800, color:"#1F1F1F",
              letterSpacing:"-0.2px", lineHeight:1.4, flex:1,
              display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" }}>
              {video.title}
            </p>
            <motion.button whileTap={{ scale:0.82 }} onClick={e => { e.stopPropagation(); onToggleBookmark(video.id); }}
              style={{ flexShrink:0, width:27, height:27, borderRadius:"50%", border:"none", cursor:"pointer",
                display:"flex", alignItems:"center", justifyContent:"center",
                background: isBookmarked ? "rgba(242,154,12,0.12)" : "rgba(200,200,200,0.12)" }}>
              <FiBookmark style={{ color:isBookmarked?T.orange:"#CCC", fontSize:12, fill:isBookmarked?T.orange:"none" }} />
            </motion.button>
          </div>
          <p style={{ fontSize:11, color:"#AAA", marginBottom:12 }}>{video.instr}</p>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              {/* Interactive Like Button */}
              <motion.button 
                whileHover={{ scale:1.1 }} 
                whileTap={{ scale:0.9 }}
                onClick={e => { e.stopPropagation(); onLike(video.id); }}
                style={{ background:"rgba(107,138,90,0.08)", border:"1px solid rgba(107,138,90,0.15)", borderRadius:100, 
                  padding:"4px 10px", display:"flex", alignItems:"center", gap:5, cursor:"pointer", color:T.olive, fontSize:10, fontWeight:700 }}
              >
                <FiThumbsUp size={11} style={{ fill: "currentColor" }} />
                <span>{video.likes}</span>
              </motion.button>
              <span style={{ fontSize:10, color:"#999", display:"flex", alignItems:"center", gap:4 }}>
                <FiEye size={11}/>
                <span>{video.views>1000?`${(video.views/1000).toFixed(1)}k`:video.views}</span>
              </span>
            </div>
            <TierBadge tier={video.tier}/>
          </div>

          {/* In-progress bar */}
          {video.pct>0 && video.pct<100 && !locked && (
            <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid rgba(239,229,216,0.7)" }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5, fontSize:10 }}>
                <span style={{ color:"#999" }}>In progress</span>
                <span style={{ fontWeight:800, color:T.orange }}>{video.pct}%</span>
              </div>
              <div style={{ height:5, background:"rgba(242,154,12,0.12)", borderRadius:100, overflow:"hidden" }}>
                <motion.div style={{ height:"100%", background:`linear-gradient(90deg,${T.orange},${T.orange2})`, borderRadius:100 }}
                  initial={{ width:0 }} animate={{ width:`${video.pct}%` }} transition={{ duration:1.2, delay:delay+0.4 }}/>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── VIDEO CARD (LIST) ────────────────────────────────────────────────────────
function VideoListRow({ video, userTier, userRank, packages=[], onPlay, onUpgrade, delay=0 }) {
  const locked = typeof video.isLocked === "boolean" ? video.isLocked : !canUserAccessContent(video.tier, userTier, userRank, packages);
  const acc = CAT_ACCENT[video.cat] || T.orange;
  return (
    <motion.div initial={{ opacity:0, x:-16 }} whileInView={{ opacity:1, x:0 }}
      viewport={{ once:true }} transition={{ delay, duration:0.45, ease:[0.22,1,0.36,1] }}
      whileHover={{ x:6 }}
      onClick={() => locked ? onUpgrade(video) : onPlay(video)}
      style={{ display:"flex", alignItems:"center", gap:16, borderRadius:22, padding:"14px 18px", cursor:"pointer",
        background:"rgba(255,255,255,0.82)", border:"1px solid rgba(255,255,255,0.95)",
        boxShadow:"0 8px 28px rgba(0,0,0,0.05)", transition:"box-shadow 0.3s" }}>

      {/* Icon thumb */}
      <div style={{ width:64, height:64, borderRadius:18, flexShrink:0, position:"relative", overflow:"hidden",
        display:"flex", alignItems:"center", justifyContent:"center",
        background: `linear-gradient(135deg,${acc}55,${acc}22)` }}>
        {video.thumb && (
          <img src={video.thumb} alt={video.title} style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover", filter: locked ? "grayscale(40%) contrast(90%) brightness(85%)" : "none" }} />
        )}
        <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", background: locked ? "rgba(0,0,0,0.4)" : "transparent", zIndex: 2 }}>
          {locked
            ? <FiLock style={{ fontSize:18, color:"#fff" }} />
            : !video.thumb
              ? video.pct===100
                ? <FiCheckCircle style={{ fontSize:26, color:T.olive }} />
                : video.pct>0
                  ? <FiRefreshCw style={{ fontSize:24, color:acc }} />
                  : <FiPlayCircle style={{ fontSize:28, color:acc }} />
              : null
          }
        </div>
      </div>

      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4, flexWrap:"wrap" }}>
          <TierBadge tier={video.tier} sm/>
          <span style={{ fontSize:9, fontWeight:800, color:acc, background:`${acc}18`, padding:"2px 7px", borderRadius:100 }}>{video.cat}</span>
          {locked && <span style={{ fontSize:9, fontWeight:800, color:T.orange2, background:"rgba(233,120,24,0.1)", padding:"2px 7px", borderRadius:100, display:"flex", alignItems:"center", gap:3 }}><FiLock size={8}/> Upgrade</span>}
        </div>
        <p style={{ fontFamily:"'Poppins',sans-serif", fontSize:14, fontWeight:800, color:"#1F1F1F",
          letterSpacing:"-0.2px", marginBottom:3, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
          {video.title}
        </p>
        <p style={{ fontSize:11, color:"#AAA" }}>{video.instr} · {video.dur}</p>
      </div>

      <div style={{ display:"flex", alignItems:"center", gap:16, flexShrink:0 }}>
        <Stars rating={video.rating}/>
        {video.pct>0 && video.pct<100 && !locked && (
          <div style={{ display:"flex", alignItems:"center", gap:5 }}>
            <ProgressRing pct={video.pct} size={28} stroke={2.5}/>
            <span style={{ fontSize:11, fontWeight:800, color:T.orange }}>{video.pct}%</span>
          </div>
        )}
        <span style={{ fontSize:11, color:"#CCC" }}>{video.views>1000?`${(video.views/1000).toFixed(1)}k`:video.views} views</span>
      </div>
    </motion.div>
  );
}

// ─── UPGRADE MODAL ────────────────────────────────────────────────────────────
function UpgradeModal({ video, packages = [], onClose }) {
  const navigate = useNavigate();
  const rawTier = video?.tier || "Premium";
  const lowTier = String(rawTier).toLowerCase().trim();
  const normKey = normalizeTier(rawTier);

  // Find dynamic package from DB
  const matchedPkg = (packages || []).find(p => {
    const pName = (p.name || "").toLowerCase().trim();
    const pType = (p.packageType || "").toLowerCase().trim();
    return pName === lowTier || pType === lowTier || pName.includes(lowTier) || (lowTier && lowTier.includes(pName));
  });

  const tierLabel = matchedPkg?.name || matchedPkg?.packageType || rawTier;
  const t = TIERS[lowTier] || TIERS[normKey] || {
    label: tierLabel,
    grad: "linear-gradient(135deg,#F29A0C,#E97818)",
    bg: "rgba(242,154,12,0.12)",
    border: "rgba(242,154,12,0.25)",
    color: "#C8660A"
  };

  // Dynamic Perks from Admin Package Features
  let perks = [];
  if (matchedPkg) {
    if (Array.isArray(matchedPkg.features) && matchedPkg.features.length > 0) {
      perks = matchedPkg.features;
    } else if (matchedPkg.featuresJson) {
      try {
        const parsed = JSON.parse(matchedPkg.featuresJson);
        if (Array.isArray(parsed) && parsed.length > 0) perks = parsed;
      } catch (e) {}
    }
  }

  if (!perks || perks.length === 0) {
    perks = UPGRADE_PERKS[normKey] || UPGRADE_PERKS[lowTier] || [
      `Full access to all ${tierLabel} training masterclasses`,
      "High Definition 1080p streaming with bookmarking",
      "Comprehensive weekly personalized progress tracking",
      "Interactive Q&A and priority community support"
    ];
  }

  // Dynamic Admin Description
  const adminDescription = matchedPkg?.description 
    ? matchedPkg.description 
    : (video?.title && video.title !== `${tierLabel} Exclusive Content`)
      ? `"${video.title}" is exclusively for ${tierLabel} subscribers. Upgrade to unlock this and all ${tierLabel} content.`
      : `Upgrade to the ${tierLabel} plan to unlock all exclusive masterclasses, live sessions, and personalized features.`;

  const priceText = matchedPkg?.price ? ` • ₹${matchedPkg.price.toLocaleString()}` : "";

  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="ui-modal-overlay"
      style={{ position:"fixed", inset:0, zIndex:50, display:"flex", alignItems:"center", justifyContent:"center", padding:24,
        background:"rgba(0,0,0,0.65)", backdropFilter:"blur(16px)" }}
      onClick={onClose}>
      <motion.div initial={{ scale:0.88, y:24 }} animate={{ scale:1, y:0 }} exit={{ scale:0.88, y:24 }}
        transition={{ type:"spring", stiffness:340, damping:28 }}
        onClick={e => e.stopPropagation()}
        style={{ width:"100%", maxWidth:520, borderRadius:32, overflow:"hidden",
          background:"rgba(255,255,255,0.98)", boxShadow:"0 40px 100px rgba(0,0,0,0.38)" }}>

        {/* Gradient header */}
        <div style={{ position:"relative", overflow:"hidden", padding:32, background:t.grad }}>
          <div style={{ position:"absolute", inset:0, backgroundImage:"radial-gradient(circle,rgba(255,255,255,0.07) 1px,transparent 0)", backgroundSize:"18px 18px" }}/>
          <div style={{ position:"absolute", top:0, right:0, width:180, height:180, borderRadius:"50%", opacity:0.1,
            background:"radial-gradient(circle,#fff,transparent)", filter:"blur(30px)", transform:"translate(30%,-30%)" }}/>
          <div style={{ position:"relative" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
              <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                <div style={{ width:44, height:44, borderRadius:16, background:"rgba(255,255,255,0.2)",
                  backdropFilter:"blur(8px)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <HiSparkles style={{ fontSize:22, color:"#fff" }}/>
                </div>
                <div>
                  <p style={{ color:"rgba(255,255,255,0.65)", fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"1.5px" }}>Upgrade Required</p>
                  <p style={{ color:"#fff", fontFamily:"'Poppins',sans-serif", fontSize:18, fontWeight:900, letterSpacing:"-0.3px" }}>{tierLabel} Plan</p>
                </div>
              </div>
              <motion.button whileHover={{ scale:1.1 }} whileTap={{ scale:0.9 }} onClick={onClose}
                style={{ width:36, height:36, borderRadius:"50%", background:"rgba(255,255,255,0.18)",
                  border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <FiX style={{ color:"#fff", fontSize:16 }}/>
              </motion.button>
            </div>
            <p style={{ color:"rgba(255,255,255,0.9)", fontSize:13, lineHeight:1.7 }}>
              {adminDescription}
            </p>
          </div>
        </div>

        <div style={{ padding:"24px 28px" }}>
          <p style={{ fontFamily:"'Poppins',sans-serif", fontSize:14, fontWeight:800, color:"#1F1F1F", marginBottom:14, letterSpacing:"-0.2px" }}>
            What you unlock with {tierLabel}
          </p>
          <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:22 }}>
            {perks.map((p, i) => (
              <motion.div key={i} initial={{ opacity:0, x:-12 }} animate={{ opacity:1, x:0 }} transition={{ delay:i*0.06 }}
                style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ width:24, height:24, borderRadius:"50%", flexShrink:0,
                  background:t.bg, border:`1px solid ${t.border}`,
                  display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <FiCheck style={{ fontSize:11, color:t.color }}/>
                </div>
                <span style={{ fontSize:13, color:"#555", fontFamily:"'Inter',sans-serif" }}>{p}</span>
              </motion.div>
            ))}
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <motion.button whileHover={{ y:-2, scale:1.01 }} whileTap={{ scale:0.97 }}
              onClick={() => navigate("/user/settings?tab=subscription")}
              style={{ flex:1, height:48, borderRadius:100, background:t.grad, border:"none", cursor:"pointer",
                color:"#fff", fontSize:13, fontWeight:800, fontFamily:"'Inter',sans-serif",
                display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                boxShadow:"0 12px 30px rgba(0,0,0,0.2)" }}>
              <HiSparkles size={15}/> Upgrade to {tierLabel}{priceText}
            </motion.button>
            <motion.button whileHover={{ y:-2 }} whileTap={{ scale:0.97 }} onClick={onClose}
              style={{ height:48, padding:"0 20px", borderRadius:100,
                background:"rgba(255,255,255,0.9)", border:"1px solid rgba(239,229,216,0.9)",
                color:"#666", cursor:"pointer", fontFamily:"'Inter',sans-serif", fontSize:13, fontWeight:600 }}>
              Not now
            </motion.button>
          </div>
          <p style={{ textAlign:"center", fontSize:11, color:"#CCC", marginTop:10, fontFamily:"'Inter',sans-serif" }}>
            Paid via cash? Contact admin for instant activation
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

function PlayerModal({ video, onVideoUpdate, onClose }) {
  const [tab, setTab]           = useState("chapters");
  const [progress, setProgress] = useState(video.pct || 0);

  const [views, setViews] = useState(video.views || 0);
  const [rating, setRating] = useState(video.rating || 4.8);
  const [userRating, setUserRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);

  const [note, setNote] = useState("");
  const [noteSavedMessage, setNoteSavedMessage] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);

  const videoRef = useRef(null);
  const lastProgressRef = useRef(video.pct || 0);

  // Increment views on modal open
  useEffect(() => {
    const updateViews = async () => {
      try {
        const res = await practiceVideoAPI.interactVideo(video.id, true);
        if (res.data?.success) {
          setViews(res.data.views);
          setRating(res.data.rating);
          if (onVideoUpdate) {
            onVideoUpdate(video.id, { views: res.data.views, rating: res.data.rating });
          }
        }
      } catch (err) {
        console.error("Failed to increment views:", err);
      }
    };
    updateViews();
  }, [video.id]);

  // Load saved notes
  useEffect(() => {
    const savedNote = localStorage.getItem(`divyayoga_video_note_${video.id}`) || "";
    setNote(savedNote);
  }, [video.id]);

  const handleRate = async (rateVal) => {
    setUserRating(rateVal);
    try {
      const res = await practiceVideoAPI.interactVideo(video.id, false, rateVal);
      if (res.data?.success) {
        setRating(res.data.rating);
        if (onVideoUpdate) {
          onVideoUpdate(video.id, { rating: res.data.rating });
        }
      }
    } catch (err) {
      console.error("Failed to submit rating:", err);
    }
  };

  const handleSaveNote = () => {
    localStorage.setItem(`divyayoga_video_note_${video.id}`, note);
    setNoteSavedMessage(true);
    setTimeout(() => setNoteSavedMessage(false), 2000);
  };

  const handleTimeUpdate = (e) => {
    const videoEl = e.currentTarget;
    if (videoEl.duration) {
      const pct = Math.round((videoEl.currentTime / videoEl.duration) * 100);
      // Throttle updates to prevent continuous parent re-renders and video stuttering
      if (pct >= lastProgressRef.current + 5 || pct === 100) {
        lastProgressRef.current = pct;
        setProgress(pct);
        if (onVideoUpdate) {
          onVideoUpdate(video.id, { pct });
        }
      }
    }
  };

  const handleEnded = () => {
    setProgress(100);
    if (onVideoUpdate) {
      onVideoUpdate(video.id, { pct: 100 });
    }
  };

  // Dynamic interactive chapters based on total minutes
  const totalMins = parseInt(video.dur) || 30;
  const totalSecs = totalMins * 60;
  const numChapters = video.chapters || 6;
  const secsPerChapter = totalSecs / numChapters;

  const formatSecs = (s) => {
    const min = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${min}:${String(sec).padStart(2, "0")}`;
  };

  const chapters = Array.from({ length: numChapters }, (_, i) => {
    const startSec = i * secsPerChapter;
    return {
      num: i + 1,
      title: CH_NAMES[i] || `Chapter ${i + 1}`,
      startTime: startSec,
      dur: formatSecs(secsPerChapter),
      startStr: formatSecs(startSec),
      done: i < Math.floor(numChapters * progress / 100)
    };
  });

  const handleChapterClick = (startTime) => {
    if (videoRef.current) {
      videoRef.current.currentTime = startTime;
      videoRef.current.play();
    }
  };

  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="ui-modal-overlay"
      style={{ position:"fixed", inset:0, zIndex:50, display:"flex", alignItems:"center", justifyContent:"center", padding:24,
        background:"rgba(0,0,0,0.72)", backdropFilter:"blur(18px)" }}
      onClick={onClose}>
      <motion.div initial={{ scale:0.88, y:28 }} animate={{ scale:1, y:0 }} exit={{ scale:0.88, y:28 }}
        transition={{ type:"spring", stiffness:300, damping:28 }}
        onClick={e => e.stopPropagation()}
        style={{ width:"100%", maxWidth:1060, borderRadius:32, overflow:"hidden",
          background:"rgba(255,255,255,0.97)", boxShadow:"0 40px 120px rgba(0,0,0,0.45)" }}>

        {/* Header */}
        <div className="tl-player-head" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"16px 24px",
          borderBottom:"1px solid rgba(239,229,216,0.7)" }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:3, height:32, borderRadius:2, background:`linear-gradient(180deg,${T.orange},${T.orange2})` }}/>
            <div>
              <p style={{ fontSize:10, fontWeight:800, color:T.orange, textTransform:"uppercase", letterSpacing:"1.2px" }}>Now Playing</p>
              <p style={{ fontFamily:"'Poppins',sans-serif", fontSize:18, fontWeight:900, color:"#1F1F1F", letterSpacing:"-0.3px" }}>{video.title}</p>
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <TierBadge tier={video.tier}/>
            <motion.button whileHover={{ scale:1.1 }} whileTap={{ scale:0.9 }} onClick={onClose}
              style={{ width:36, height:36, borderRadius:"50%", background:"rgba(0,0,0,0.07)", border:"none", cursor:"pointer",
                display:"flex", alignItems:"center", justifyContent:"center" }}>
              <FiX style={{ fontSize:17, color:"#555" }}/>
            </motion.button>
          </div>
        </div>

        <div className="tl-player-grid" style={{ display:"grid", gridTemplateColumns:"1fr 320px" }}>
          {/* Player screen */}
          <div>
            <div className="tl-player-screen" style={{ height:340, position:"relative", background:"black", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <video 
                ref={videoRef}
                src={video.videoUrl} 
                controls 
                controlsList="nodownload"
                onContextMenu={(e) => e.preventDefault()}
                autoPlay 
                preload="auto"
                playsInline
                onWaiting={() => setIsBuffering(true)}
                onPlaying={() => setIsBuffering(false)}
                onCanPlay={() => setIsBuffering(false)}
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleEnded}
                style={{ width: "100%", height: "100%" }}
              />
              {isBuffering && (
                <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", background:"rgba(0,0,0,0.55)", backdropFilter:"blur(4px)", zIndex:10 }}>
                  <div style={{ width:36, height:36, borderRadius:"50%", border:"3px solid rgba(255,255,255,0.2)", borderTopColor:T.orange, animation:"spin 0.8s linear infinite", marginBottom:8 }} />
                  <span style={{ color:"#fff", fontSize:11, fontWeight:700, fontFamily:"'Inter',sans-serif" }}>Buffering HD Stream...</span>
                </div>
              )}
            </div>

            {/* Video meta */}
            <div className="tl-player-meta" style={{ padding:"18px 22px", borderBottom:"1px solid rgba(239,229,216,0.7)" }}>
              <p style={{ fontSize:13, color:"#666", lineHeight:1.7, marginBottom:14 }}>{video.desc}</p>
              <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <div style={{ width:36, height:36, borderRadius:12, background:`linear-gradient(135deg,${T.orange},${T.orange2})`,
                    display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:12, fontWeight:800, fontFamily:"'Poppins',sans-serif" }}>
                    {video.instr.split(" ").map(w=>w[0]).join("")}
                  </div>
                  <div>
                    <p style={{ fontSize:12, fontWeight:700, color:"#1F1F1F" }}>{video.instr}</p>
                    <p style={{ fontSize:10, color:"#CCC" }}>Lead Instructor</p>
                  </div>
                </div>
                
                {/* Dynamic Rating Star Interaction component */}
                <div style={{ display:"flex", gap:8, marginLeft:"auto", alignItems:"center" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:2, marginRight:8 }}>
                    {[1,2,3,4,5].map(starNum => {
                      const isLit = starNum <= (hoverRating || userRating || Math.round(rating));
                      return (
                        <FiStar 
                          key={starNum}
                          style={{ 
                            color: isLit ? T.orange : "#CCC", 
                            fontSize:15, 
                            fill: isLit ? T.orange : "none",
                            cursor:"pointer",
                            transition:"all 0.15s"
                          }} 
                          onMouseEnter={() => setHoverRating(starNum)}
                          onMouseLeave={() => setHoverRating(0)}
                          onClick={() => handleRate(starNum)}
                        />
                      );
                    })}
                    <span style={{ fontSize:11, fontWeight:800, color:T.orange, fontFamily:"'Inter',sans-serif", marginLeft:4 }}>
                      {rating.toFixed(1)} ({views} views)
                    </span>
                  </div>

                  <motion.button whileHover={{ scale:1.04 }} whileTap={{ scale:0.95 }}
                    style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 14px", borderRadius:100,
                      background:"rgba(242,154,12,0.1)", color:"#C8660A", border:"none", cursor:"pointer",
                      fontSize:11, fontWeight:700, fontFamily:"'Inter',sans-serif" }}>
                    <FiThumbsUp size={11}/> Helpful
                  </motion.button>
                  <motion.button whileHover={{ scale:1.04 }} whileTap={{ scale:0.95 }}
                    onClick={() => {
                      practiceVideoAPI.toggleBookmark(video.id).catch(() => null);
                      toast.success("Video bookmarked to library!");
                    }}
                    style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 14px", borderRadius:100,
                      background:"rgba(242,154,12,0.1)", color:T.orange, border:"none", cursor:"pointer",
                      fontSize:11, fontWeight:700, fontFamily:"'Inter',sans-serif" }}>
                    <FiBookmark size={11}/> Bookmark
                  </motion.button>
                </div>
              </div>
            </div>
          </div>

          {/* Side panel */}
          <div className="tl-player-side" style={{ borderLeft:"1px solid rgba(239,229,216,0.7)", height:480, overflowY:"auto" }}>
            <div style={{ display:"flex", borderBottom:"1px solid rgba(239,229,216,0.7)" }}>
              {["chapters","notes"].map(t => (
                <button key={t} onClick={() => setTab(t)}
                  style={{ flex:1, padding:"14px 8px", background:"transparent", border:"none", cursor:"pointer",
                    fontSize:12, fontWeight:700, fontFamily:"'Inter',sans-serif",
                    color:tab===t?T.orange:"#CCC",
                    borderBottom:tab===t?`2px solid ${T.orange}`:"2px solid transparent",
                    display:"flex", alignItems:"center", justifyContent:"center", gap:5 }}>
                  {t==="chapters" ? <FiList size={13}/> : <FiFileText size={13}/>}
                  {t}
                </button>
              ))}
            </div>
            
            {/* Dynamic seekable chapters list */}
            {tab==="chapters" && (
              <div style={{ padding:"10px 8px" }}>
                <p style={{ fontSize:10, color:"#AAA", padding:"4px 10px", margin:0 }}>Tap a chapter to skip to timestamp</p>
                {chapters.map(ch => (
                  <motion.div key={ch.num} whileHover={{ x:5, background:"rgba(242,154,12,0.05)" }}
                    onClick={() => handleChapterClick(ch.startTime)}
                    style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 10px", borderRadius:14, cursor:"pointer", transition:"all 0.2s" }}>
                    <div style={{ width:22, height:22, borderRadius:"50%", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center",
                      background:ch.done?`linear-gradient(135deg,${T.olive},${T.olive2})`:"rgba(200,200,200,0.18)" }}>
                      {ch.done ? <FiCheck style={{ color:"#fff", fontSize:10 }}/> : <span style={{ fontSize:9, fontWeight:800, color:"#CCC" }}>{ch.num}</span>}
                    </div>
                    <p style={{ flex:1, fontSize:12, fontWeight:600, color:"#1F1F1F", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{ch.title}</p>
                    <span style={{ fontSize:10, color:T.orange, fontWeight:700, flexShrink:0 }}>{ch.startStr}</span>
                  </motion.div>
                ))}
              </div>
            )}
            
            {/* Functional timestamped notes persistence */}
            {tab==="notes" && (
              <div style={{ padding:16 }}>
                <p style={{ fontSize:11, color:"#AAA", marginBottom:10 }}>Personal practice notes</p>
                <textarea rows={7} placeholder={`Notes for "${video.title}"…\ne.g. 12:34 · alignment checkpoint`}
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  style={{ width:"100%", borderRadius:14, padding:"12px 14px", fontSize:12,
                    border:"1px solid rgba(239,229,216,0.8)", background:"rgba(255,248,238,0.7)",
                    fontFamily:"'Inter',sans-serif", color:"#333", lineHeight:1.7, resize:"none", outline:"none" }}/>
                
                {noteSavedMessage && (
                  <p style={{ fontSize:11, color:T.olive, fontWeight:700, marginTop:6, textAlign:"center" }}>✓ Notes saved successfully!</p>
                )}

                <motion.button whileHover={{ y:-1 }} whileTap={{ scale:0.97 }}
                  onClick={handleSaveNote}
                  style={{ marginTop:10, width:"100%", height:38, borderRadius:100,
                    background:`linear-gradient(135deg,${T.orange},${T.orange2})`, border:"none",
                    cursor:"pointer", color:"#fff", fontFamily:"'Inter',sans-serif", fontSize:12, fontWeight:800 }}>
                  Save Note
                </motion.button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function TrainingLibraryPage() {
  const { packages, tiers: dynamicTiers } = useDynamicPackages();
  const { triggerCheckInRestriction } = useCheckIn();

  const [accountRank, setAccountRank] = useState(null);
  const [userTier,    setUserTier]    = useState("standard");
  const [cat,         setCat]         = useState("All");
  const [tierFilter,  setTierFilter]  = useState("all");
  const [search,      setSearch]      = useState("");
  const [sort,        setSort]        = useState("popular");
  const [viewMode,    setViewMode]    = useState("grid");
  const [activeVideo, setActiveVideo] = useState(null);
  const [upgradeVideo,setUpgradeVideo]= useState(null);
  const [videos,      setVideos]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [bookmarkedIds, setBookmarkedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const userRank = accountRank !== null ? accountRank : getTierRank(userTier, packages);

  const gridContainerRef = useRef(null);
  const continueScrollRef = useRef(null);
  const [columnsCount, setColumnsCount] = useState(3);
  const [hasContinueOverflow, setHasContinueOverflow] = useState(false);
  const [expandedContinue, setExpandedContinue] = useState(false);
  const lastSyncedPctRef = useRef({});

  useEffect(() => {
    const handleResize = () => {
      if (gridContainerRef.current) {
        const width = gridContainerRef.current.clientWidth;
        const cardMinWidth = 230;
        const gap = 16;
        const cols = Math.max(1, Math.floor((width + gap) / (cardMinWidth + gap)));
        setColumnsCount(cols);
      }
    };

    handleResize();

    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });

    if (gridContainerRef.current) {
      resizeObserver.observe(gridContainerRef.current);
    }

    window.addEventListener("resize", handleResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const continueWatching = useMemo(() => {
    return videos.filter(v => v.pct > 0 && v.pct < 100 && canUserAccessContent(v.tier, userTier, userRank, packages));
  }, [videos, userTier, userRank, packages]);

  useEffect(() => {
    const checkOverflow = () => {
      if (continueScrollRef.current) {
        const el = continueScrollRef.current;
        setHasContinueOverflow(el.scrollWidth > el.clientWidth + 5);
      }
    };

    // Check immediately and on layout settling
    checkOverflow();
    const timer = setTimeout(checkOverflow, 150);

    let resizeObs;
    if (continueScrollRef.current) {
      resizeObs = new ResizeObserver(() => checkOverflow());
      resizeObs.observe(continueScrollRef.current);
    }
    window.addEventListener("resize", checkOverflow);

    return () => {
      clearTimeout(timer);
      if (resizeObs) resizeObs.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, [continueWatching, expandedContinue]);

  const itemsPerPage = useMemo(() => {
    if (viewMode === "list") return 9;
    return Math.max(3, columnsCount * 3); // Auto pagination: EXACTLY 3 ROWS per screen
  }, [viewMode, columnsCount]);

  useEffect(() => {
    setCurrentPage(1);
  }, [cat, tierFilter, search, sort, itemsPerPage]);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const [res, progressRes, bookmarksRes] = await Promise.all([
          practiceVideoAPI.getVideos(),
          practiceVideoAPI.getProgress().catch(() => ({ data: { success: false } })),
          practiceVideoAPI.getBookmarks().catch(() => ({ data: { success: false } }))
        ]);

        if (res.data?.success) {
          const progressData = progressRes.data?.success ? progressRes.data.data : {};
          const bms = bookmarksRes.data?.success ? bookmarksRes.data.data : [];
          setBookmarkedIds(bms);
          
          const formatted = res.data.data.map(vid => {
            const vidId = vid.id || vid.Id;
            const rawVal = progressData[vidId.toString()];
            let pctVal = 0;
            if (typeof rawVal === "number") pctVal = rawVal;
            else if (typeof rawVal === "object" && rawVal !== null) pctVal = rawVal.progress ?? rawVal.Progress ?? 0;
            
            // Seed last synced values
            lastSyncedPctRef.current[vidId] = pctVal;

            let descText = vid.description || vid.Description || "No description provided.";
            let instructorName = "Instructor TBA";
            let durationString = `${vid.durationInMinutes || vid.DurationInMinutes || 30} min`;
            let viewsCount = 0;
            let ratingValue = 0.0;
            let likesCount = 0;

            try {
              const parsed = JSON.parse(vid.description || vid.Description);
              if (parsed && typeof parsed === "object") {
                descText = parsed.desc || "No description provided.";
                instructorName = parsed.instructor || "Instructor TBA";
                durationString = parsed.durationStr ? `${parsed.durationStr} min` : `${vid.durationInMinutes || vid.DurationInMinutes || 30} min`;
                if (parsed.views !== undefined) viewsCount = parsed.views;
                if (parsed.rating !== undefined) ratingValue = parsed.rating;
                if (parsed.likes !== undefined) likesCount = parsed.likes;
              }
            } catch (e) {
              // ignore and fallback
            }

            return {
              id: vidId,
              title: vid.title || vid.Title,
              instr: instructorName,
              dur: durationString,
              cat: vid.category || vid.Category,
              tier: vid.tier || vid.Tier || "Free",
              isLocked: vid.isLocked ?? vid.IsLocked,
              rating: ratingValue,
              views: viewsCount,
              likes: likesCount,
              pct: pctVal,
              chapters: 6,
              desc: descText,
              videoUrl: vid.videoUrl || vid.VideoUrl,
              thumb: vid.thumbnailUrl || vid.ThumbnailUrl || "/assets/poster-fallback.jpg"
            };
          });
          setVideos(formatted);
        }
      } catch (err) {
        console.error("Failed to load practice videos:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();

    userAPI.getProfile().then(profileRes => {
      if (profileRes.data?.data) {
        const data = profileRes.data.data;
        const rankVal = data.userRank ?? data.accessLevel ?? data.activeSubscription?.accessLevel;
        if (typeof rankVal === "number") {
          setAccountRank(rankVal);
        }
        const pTier = data.tier || data.activeSubscription?.packageName || data.activeSubscription?.tier || "Free";
        setUserTier(pTier);
      }
    }).catch(() => {});
  }, []);

  const handleVideoUpdate = async (videoId, updates) => {
    setVideos(prev => prev.map(v => v.id === videoId ? { ...v, ...updates } : v));
    if (updates.pct !== undefined) {
      const lastSynced = lastSyncedPctRef.current[videoId] || 0;
      const newPct = Math.round(updates.pct);

      // Throttling: Only send updates to server if it changed by >= 5%, or reaches 100%
      if (Math.abs(newPct - lastSynced) >= 5 || newPct === 100) {
        lastSyncedPctRef.current[videoId] = newPct;
        try {
          await practiceVideoAPI.updateProgress({ videoId: Number(videoId), progress: newPct });
          if (newPct === 100 || (newPct >= 50 && lastSynced < 50)) {
            const vObj = videos.find(v => v.id === Number(videoId));
            const durationMins = parseInt(vObj?.dur) || 30;
            const minsToLog = Math.max(1, Math.round(durationMins * (newPct === 100 ? 0.5 : 0.5)));
            await userAPI.logMindfulness({
              minutes: minsToLog,
              source: "Video",
              activityName: vObj?.title || "Practice Video"
            });
          }
        } catch (err) {
          console.error("Failed to sync video progress to server:", err);
        }
      }
    }
  };

  const handleToggleBookmark = async (videoId) => {
    try {
      const res = await practiceVideoAPI.toggleBookmark(videoId);
      if (res.data?.success) {
        setBookmarkedIds(prev => 
          res.data.isBookmarked 
            ? [...prev, videoId] 
            : prev.filter(id => id !== videoId)
        );
      }
    } catch (err) {
      console.error("Failed to toggle bookmark:", err);
    }
  };

  const handleLikeVideo = async (videoId) => {
    try {
      const res = await practiceVideoAPI.interactVideo(videoId, false, null, true);
      if (res.data?.success) {
        setVideos(prev => prev.map(v => {
          if (v.id === videoId) {
            return { ...v, likes: res.data.likes || (v.likes + 1) };
          }
          return v;
        }));
      }
    } catch (err) {
      console.error("Failed to like video:", err);
    }
  };

  const filtered = videos.filter(v => {
    const matchCat    = cat === "All" || (cat === "Bookmarked" ? bookmarkedIds.includes(v.id) : v.cat === cat);
    const matchSearch = v.title.toLowerCase().includes(search.toLowerCase()) || v.instr.toLowerCase().includes(search.toLowerCase());
    
    const vTier = (v.tier || "").toLowerCase().trim();
    const fTier = (tierFilter || "all").toLowerCase().trim();

    let matchTier = fTier === "all" || vTier === fTier;
    if (!matchTier && fTier !== "all") {
      if (fTier === "ytt" || fTier === "ytt pro" || fTier === "ytt module") {
        matchTier = vTier.includes("ytt") || vTier === "pro";
      } else if (fTier === "pro" || fTier === "pro plan") {
        matchTier = vTier === "pro" || vTier.includes("ytt");
      } else {
        matchTier = vTier === fTier || vTier.includes(fTier) || fTier.includes(vTier);
      }
    }

    return matchCat && matchSearch && matchTier;
  }).sort((a,b) => {
    if (sort==="rating")   return b.rating-a.rating;
    if (sort==="newest")   return b.id-a.id;
    if (sort==="progress") return b.pct-a.pct;
    return b.views-a.views;
  });

  const startIndex = (currentPage - 1) * itemsPerPage;

  const paginatedVideos = useMemo(() => {
    return filtered.slice(startIndex, startIndex + itemsPerPage);
  }, [filtered, startIndex, itemsPerPage]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const lockedCount  = filtered.filter(v => !canUserAccessContent(v.tier, userTier, userRank, packages)).length;
  const accessCount  = filtered.filter(v => canUserAccessContent(v.tier, userTier, userRank, packages)).length;

  const stats = useMemo(() => {
    const watched = videos.filter(v => v.pct > 0).length;
    const completed = videos.filter(v => v.pct === 100).length;
    
    const totalSeconds = videos.reduce((acc, v) => {
      let minVal = 30;
      try {
        const cleanDur = v.dur.replace(" min", "").trim();
        if (cleanDur.includes(":")) {
          const parts = cleanDur.split(":");
          if (parts.length === 3) {
            minVal = parseInt(parts[0]) * 60 + parseInt(parts[1]) + parseInt(parts[2]) / 60;
          } else {
            minVal = parseInt(parts[0]) + parseInt(parts[1]) / 60;
          }
        } else {
          minVal = parseInt(cleanDur);
        }
      } catch (e) {}

      const totalSec = Math.round(minVal * 60);
      return acc + Math.round((v.pct / 100) * totalSec);
    }, 0);

    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    const watchTimeStr = `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;

    const completionRate = watched > 0 ? Math.round((completed / watched) * 100) : 0;

    const rank = getTierRank(userTier, packages);
    const tierVideos = videos.filter(v => getTierRank(v.tier, packages) <= rank);
    const tierTotal = tierVideos.length;
    const watchedInTier = tierVideos.filter(v => v.pct > 0).length;

    return {
      watched,
      watchTime: watchTimeStr,
      completed,
      completionRate: `${completionRate}%`,
      tierTotal,
      watchedInTier
    };
  }, [videos, userTier]);

  if (loading) {
    return (
      <UserDashboardLayout>
        <Topbar />
        <BrandLoader 
          message="Loading Training Library..." 
          subMessage="Fetching your practice sessions & yoga videos" 
        />
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <link rel="preconnect" href="https://fonts.googleapis.com"/>
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@700;800;900&family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet"/>
      <style>{`
        @media (max-width: 640px) {
          .tl-tier-rail { flex-wrap: nowrap !important; overflow-x: auto !important; -webkit-overflow-scrolling: touch; scrollbar-width: none; touch-action: pan-x pan-y; padding: 12px 14px !important; }
          .tl-tier-rail::-webkit-scrollbar { display: none; }
          .tl-tier-rail > * { flex-shrink: 0 !important; white-space: nowrap; }
          .tl-tier-rail > .tl-tier-count { margin-left: 8px !important; }
        }
      `}</style>
      <div style={{ fontFamily:"'Inter',sans-serif" }}>

        {/* Orbs */}
        <div style={{ position:"fixed", width:500, height:500, borderRadius:"50%", background:"radial-gradient(circle,rgba(242,154,12,0.08),transparent 70%)", filter:"blur(80px)", top:-150, right:-100, pointerEvents:"none", zIndex:0 }}/>
        <div style={{ position:"fixed", width:380, height:380, borderRadius:"50%", background:"radial-gradient(circle,rgba(107,138,90,0.07),transparent 70%)", filter:"blur(80px)", bottom:-80, left:-80, pointerEvents:"none", zIndex:0 }}/>

        <div className="w-full min-w-0" style={{ position:"relative", zIndex:1 }}>
          <Topbar/>
          {/* ── HERO ── */}
          <motion.div initial={{ opacity:0, y:24 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.65 }}
            className="rounded-[36px] p-5 sm:p-7 md:p-9 mb-5 relative overflow-hidden"
            style={glass}>
            <div style={{ position:"absolute", inset:0, backgroundImage:"radial-gradient(circle,rgba(0,0,0,0.025) 1px,transparent 0)", backgroundSize:"22px 22px", borderRadius:"inherit", pointerEvents:"none" }}/>
            <div style={{ position:"absolute", top:0, right:0, width:220, height:220, background:`radial-gradient(circle,${T.orange}18,transparent)`, filter:"blur(40px)", pointerEvents:"none" }}/>

            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:24, flexWrap:"wrap", position:"relative" }}>
              <div style={{ flex:1, minWidth:280 }}>
                {/* Title row */}
                <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:18, flexWrap:"wrap" }}>
                  <div style={{ width:44, height:44, borderRadius:16, background:`linear-gradient(135deg,${T.orange},${T.orange2})`,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    boxShadow:`0 8px 22px rgba(242,154,12,0.42)` }}>
                    <FiPlayCircle style={{ color:"#fff", fontSize:22 }}/>
                  </div>
                  <div>
                    <p style={{ fontSize:10, fontWeight:800, color:T.orange, textTransform:"uppercase", letterSpacing:"1.5px" }}>Training Library</p>
                    <p style={{ fontSize:11, color:"#AAA" }}>{accessCount} accessible · {lockedCount>0?`${lockedCount} require upgrade`:"all unlocked"}</p>
                  </div>
                  <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:6, padding:"6px 14px", borderRadius:100,
                    background: (TIERS[userTier?.toLowerCase()] || TIERS[normalizeTier(userTier)] || TIERS.free).bg, 
                    border: `1px solid ${(TIERS[userTier?.toLowerCase()] || TIERS[normalizeTier(userTier)] || TIERS.free).border}` }}>
                    <FiShield style={{ color: (TIERS[userTier?.toLowerCase()] || TIERS[normalizeTier(userTier)] || TIERS.free).color, fontSize:12 }}/>
                    <span style={{ fontSize:11, fontWeight:800, color: (TIERS[userTier?.toLowerCase()] || TIERS[normalizeTier(userTier)] || TIERS.free).color }}>
                      {(TIERS[userTier?.toLowerCase()] || TIERS[normalizeTier(userTier)])?.label || userTier || "Free"}
                    </span>
                  </div>
                </div>

                <h1 className="text-[26px] sm:text-[32px] lg:text-[36px]" style={{ fontFamily:"'Poppins',sans-serif", fontWeight:900, color:"#1F1F1F",
                  letterSpacing:"-1.5px", lineHeight:1.06, marginBottom:18 }}>
                  Your{" "}
                  <span style={{ background:`linear-gradient(135deg,${T.orange},${T.orange2})`, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
                    yoga universe,
                  </span>
                  {" "}on demand.
                </h1>

                {/* Search + controls */}
                <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10, flex:1, minWidth:220, borderRadius:14, padding:"10px 14px",
                    background:"rgba(255,255,255,0.88)", border:"1px solid rgba(239,229,216,0.8)" }}>
                    <FiSearch style={{ color:T.orange, fontSize:15, flexShrink:0 }}/>
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search videos, instructors…"
                      style={{ flex:1, background:"transparent", border:"none", outline:"none", fontSize:13, color:"#1F1F1F", fontFamily:"'Inter',sans-serif" }}/>
                    {search && <motion.button whileTap={{ scale:0.9 }} onClick={() => setSearch("")}
                      style={{ background:"none", border:"none", cursor:"pointer" }}>
                      <FiX style={{ fontSize:13, color:"#CCC" }}/>
                    </motion.button>}
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:8, borderRadius:14, padding:"10px 14px",
                    background:"rgba(255,255,255,0.88)", border:"1px solid rgba(239,229,216,0.8)" }}>
                    <FiFilter style={{ color:"#AAA", fontSize:13 }}/>
                    <select value={sort} onChange={e => setSort(e.target.value)}
                      style={{ background:"transparent", border:"none", outline:"none", fontSize:12, fontWeight:700, color:"#555", fontFamily:"'Inter',sans-serif", cursor:"pointer" }}>
                      <option value="popular">Most Popular</option>
                      <option value="rating">Top Rated</option>
                      <option value="newest">Newest First</option>
                      <option value="progress">In Progress</option>
                    </select>
                  </div>
                  {/* Grid/list toggle */}
                  <div style={{ display:"flex", borderRadius:14, overflow:"hidden", border:"1px solid rgba(239,229,216,0.8)" }}>
                    {["grid","list"].map(m => (
                      <button key={m} onClick={() => setViewMode(m)}
                        style={{ width:40, height:40, display:"flex", alignItems:"center", justifyContent:"center",
                          background:viewMode===m?`linear-gradient(135deg,${T.orange},${T.orange2})`:"rgba(255,255,255,0.88)",
                          border:"none", cursor:"pointer", transition:"all 0.2s" }}>
                        {m==="grid"
                          ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={viewMode===m?"#fff":"#888"} strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                          : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={viewMode===m?"#fff":"#888"} strokeWidth="2.5" strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                        }
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* ── STATS STRIP ── */}
          <motion.div initial={{ opacity:0, y:18 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.08 }}
            className="ud-kpi"
            style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", borderRadius:24, overflow:"hidden",
              border:"1px solid rgba(255,255,255,0.9)", boxShadow:"0 12px 40px rgba(0,0,0,0.05)", marginBottom:18 }}>
            {[
              { icon:FiEye,        val:stats.watched,  lbl:"Videos Watched",  accent:T.orange, bg:"linear-gradient(145deg,#FFF4E8,#FFF8EE)" },
              { icon:FiClock,      val:stats.watchTime,  lbl:"Watch Time",      accent:T.olive,  bg:"linear-gradient(145deg,#F5FAF2,#F9FDF5)" },
              { icon:FiCheck,      val:stats.completed,   lbl:"Completed",       accent:T.orange, bg:"linear-gradient(145deg,#FFF4E8,#FFF8EE)" },
              { icon:FiTrendingUp, val:stats.completionRate,  lbl:"Completion Rate", accent:T.olive,  bg:"linear-gradient(145deg,#F5FAF2,#F9FDF5)" },
              { icon:FiShield,     val:`${stats.watchedInTier} / ${stats.tierTotal}`, lbl:"Plan Progress", accent:T.orange2, bg:"linear-gradient(145deg,#FFF4E8,#FFF8EE)" }
            ].map((s,i) => (
              <motion.div key={s.lbl} whileHover={{ background:"rgba(255,255,255,0.96)" }}
                style={{ position:"relative", padding:"22px 20px", display:"flex", alignItems:"center", gap:14,
                  background:s.bg, borderRight:i<4?"1px solid rgba(239,229,216,0.5)":"none" }}>
                <div style={{ position:"absolute", bottom:0, left:0, right:0, height:2, background:`linear-gradient(90deg,${s.accent},${s.accent}88)` }}/>
                <div className="ud-kpi-icon" style={{ width:36, height:36, borderRadius:12, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center",
                  background:`linear-gradient(135deg,${s.accent},${s.accent}CC)`, boxShadow:`0 6px 16px ${s.accent}40` }}>
                  <s.icon style={{ color:"#fff", fontSize:16 }}/>
                </div>
                <div>
                  <p className="ud-kpi-val" style={{ fontFamily:"'Poppins',sans-serif", fontSize:26, fontWeight:900, color:"#1F1F1F", letterSpacing:"-1px", lineHeight:1 }}>{s.val}</p>
                  <p className="ud-kpi-lbl" style={{ fontSize:10, fontWeight:700, color:"#BBB", textTransform:"uppercase", letterSpacing:"0.7px", marginTop:3 }}>{s.lbl}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* ── TIER FILTER RAIL ── */}
          <motion.div initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.11 }}
            className="tl-tier-rail"
            style={{ borderRadius:22, padding:"12px 16px", marginBottom:18, display:"flex", fontStyle:"normal", alignItems:"center", gap:10, flexWrap:"wrap",
              background:"rgba(255,255,255,0.75)", border:"1px solid rgba(255,255,255,0.9)", boxShadow:"0 8px 28px rgba(0,0,0,0.04)" }}>
            <span style={{ fontSize:10, fontWeight:800, color:"#AAA", textTransform:"uppercase", letterSpacing:"1.2px", flexShrink:0 }}>Filter by plan:</span>
            {[{ key:"all", label:"All Content" }, ...dynamicTiers.map(t => ({ key:t.toLowerCase(), label:t }))].map(({ key, label }) => {
              const normKey = normalizeTier(label);
              const lowKey = key.toLowerCase();
              const t = TIERS[lowKey] || TIERS[normKey] || { grad:`linear-gradient(135deg,${T.orange},${T.orange2})`, rank: 0 };
              const isActive = tierFilter === key || tierFilter === label || tierFilter.toLowerCase() === lowKey;
              const isAccessible = key === "all" || canUserAccessContent(label, userTier, userRank, packages);
              const isLocked = !isAccessible;
              return (
                <motion.button key={key} 
                  onClick={() => {
                    setTierFilter(key);
                  }}
                  whileTap={{ scale:0.94 }} whileHover={{ y:-2 }}
                  style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 16px", borderRadius:100, cursor:"pointer",
                    fontFamily:"'Inter',sans-serif", transition:"all 0.22s", border:"none",
                    background:isActive?(t?t.grad:`linear-gradient(135deg,${T.orange},${T.orange2})`):"rgba(255,255,255,0.85)",
                    ...(isActive?{}:{ border:"1px solid rgba(239,229,216,0.8)" }),
                    boxShadow:isActive?"0 6px 18px rgba(0,0,0,0.15)":"none" }}>
                  {isLocked && <FiLock style={{ fontSize:10, color:isActive?"rgba(255,255,255,0.7)":"#CCC" }}/>}
                  <span style={{ fontSize:12, fontWeight:800, color:isActive?"#fff":"#666" }}>{label}</span>
                  {isLocked && !isActive && (
                    <span 
                      onClick={(e) => {
                        e.stopPropagation();
                        setUpgradeVideo({ title: `${label} Exclusive Content`, tier: label });
                      }}
                      style={{ fontSize:9, fontWeight:800, color:T.orange2, background:"rgba(233,120,24,0.12)", padding:"1px 6px", borderRadius:100 }}>
                      Upgrade
                    </span>
                  )}
                </motion.button>
              );
            })}
            <span className="tl-tier-count" style={{ marginLeft:"auto", fontSize:12, fontWeight:600, color:"#CCC" }}>
              {accessCount} accessible · {lockedCount} locked
            </span>
          </motion.div>

          {/* ── CONTINUE WATCHING ── */}
          <AnimatePresence>
            {continueWatching.length>0 && (
              <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }} transition={{ delay:0.14 }}
                style={{ marginBottom:24 }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
                  <div>
                    <h2 style={{ fontFamily:"'Poppins',sans-serif", fontSize:20, fontWeight:900, color:"#1F1F1F", letterSpacing:"-0.5px" }}>Continue Watching</h2>
                    <p style={{ fontSize:12, color:"#AAA", marginTop:2 }}>Pick up where you left off</p>
                  </div>
                  {(hasContinueOverflow || expandedContinue) && (
                    <motion.button 
                      whileHover={{ x:3 }} 
                      whileTap={{ scale:0.96 }}
                      onClick={() => setExpandedContinue(!expandedContinue)}
                      style={{ background:"none", border:"none", cursor:"pointer", fontSize:12, fontWeight:700, color:T.orange,
                        display:"flex", alignItems:"center", gap:5, fontFamily:"'Inter',sans-serif" }}>
                      {expandedContinue ? "Show less" : `View all (${continueWatching.length})`}
                      <FiChevronRight size={13} style={{ transform: expandedContinue ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.22s" }}/>
                    </motion.button>
                  )}
                </div>
                <div 
                  ref={continueScrollRef}
                  style={expandedContinue ? {
                    display:"grid",
                    gridTemplateColumns:"repeat(auto-fill, minmax(238px, 1fr))",
                    gap:14,
                    paddingBottom:6
                  } : {
                    display:"flex",
                    gap:14,
                    overflowX:"auto",
                    paddingBottom:6,
                    scrollbarWidth:"none"
                  }}>
                  {continueWatching.map((v,i) => (
                    <motion.div key={v.id} initial={{ opacity:0, x:16 }} animate={{ opacity:1, x:0 }}
                      transition={{ delay:i*0.05 }} whileHover={{ y:-7 }}
                      onClick={() => triggerCheckInRestriction(() => setActiveVideo(v))}
                      style={{ 
                        flexShrink: expandedContinue ? 1 : 0, 
                        width: expandedContinue ? "100%" : 238, 
                        borderRadius:22, 
                        overflow:"hidden", 
                        cursor:"pointer",
                        background:"rgba(255,255,255,0.85)", 
                        border:"1px solid rgba(255,255,255,0.95)",
                        boxShadow:"0 14px 42px rgba(0,0,0,0.07)", 
                        transition:"box-shadow 0.3s" 
                      }}>
                      <div style={{ height:128, position:"relative", display:"flex", alignItems:"center", justifyContent:"center",
                        background:`linear-gradient(145deg,${CAT_ACCENT[v.cat]||T.orange}BB,${CAT_ACCENT[v.cat]||T.orange}55)` }}>
                        <FiPlayCircle style={{ fontSize:42, color:"rgba(255,255,255,0.45)" }}/>
                        <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                          <div style={{ width:40, height:40, borderRadius:"50%", background:"rgba(255,255,255,0.92)",
                            display:"flex", alignItems:"center", justifyContent:"center",
                            boxShadow:"0 6px 18px rgba(0,0,0,0.25)" }}>
                            <FiPlay style={{ color:T.orange, fontSize:18, marginLeft:2 }}/>
                          </div>
                        </div>
                        <div style={{ position:"absolute", bottom:0, left:0, right:0, height:3, background:"rgba(255,255,255,0.2)" }}>
                          <div style={{ height:"100%", width:`${v.pct}%`, background:T.orange }}/>
                        </div>
                      </div>
                      <div style={{ padding:"12px 14px" }}>
                        <p style={{ fontFamily:"'Poppins',sans-serif", fontSize:13, fontWeight:800, color:"#1F1F1F", marginBottom:6,
                          whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{v.title}</p>
                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                          <span style={{ fontSize:11, color:"#AAA" }}>{v.instr}</span>
                          <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                            <ProgressRing pct={v.pct} size={26} stroke={2.5}/>
                            <span style={{ fontSize:11, fontWeight:800, color:T.orange }}>{v.pct}%</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── CATEGORY TABS ── */}
          <motion.div initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.16 }}
            style={{ display:"flex", alignItems:"center", gap:8, marginBottom:18, overflowX:"auto", paddingBottom:4, scrollbarWidth:"none" }}>
            {CATS.map(c => (
              <motion.button key={c} onClick={() => setCat(c)} whileTap={{ scale:0.94 }} animate={{ y:cat===c?-2:0 }}
                style={{ padding:"9px 20px", borderRadius:100, fontSize:13, fontWeight:800, whiteSpace:"nowrap",
                  border:"none", outline:"none", cursor:"pointer", fontFamily:"'Inter',sans-serif", transition:"all 0.22s",
                  background:cat===c?`linear-gradient(135deg,${T.orange},${T.orange2})`:"rgba(255,255,255,0.82)",
                  color:cat===c?"#fff":"#888",
                  boxShadow:cat===c?`0 8px 22px rgba(242,154,12,0.38)`:"none",
                  ...(cat!==c?{ border:"1px solid rgba(239,229,216,0.8)" }:{}) }}>
                {c}
              </motion.button>
            ))}
            <span style={{ marginLeft:"auto", fontSize:12, fontWeight:600, color:"#CCC", flexShrink:0 }}>
              {filtered.length} video{filtered.length!==1?"s":""}
            </span>
          </motion.div>

          {/* ── VIDEO GRID / LIST ── */}
          <div ref={gridContainerRef}>
            <AnimatePresence mode="wait">
              <motion.div key={`${cat}-${tierFilter}-${search}-${sort}-${viewMode}-${currentPage}`}
                initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }} transition={{ duration:0.3 }}
                style={viewMode==="grid"
                  ? { display:"grid", gap:16, gridTemplateColumns:"repeat(auto-fill,minmax(230px,1fr))" }
                  : { display:"flex", flexDirection:"column", gap:10 }
                }>
                {paginatedVideos.map((v,i) => viewMode==="grid"
                  ? <VideoCard key={v.id} video={v} userTier={userTier} userRank={userRank} packages={packages} onPlay={(videoObj) => triggerCheckInRestriction(() => setActiveVideo(videoObj))} onUpgrade={setUpgradeVideo} isBookmarked={bookmarkedIds.includes(v.id)} onToggleBookmark={handleToggleBookmark} onLike={handleLikeVideo} delay={i*0.04}/>
                  : <VideoListRow key={v.id} video={v} userTier={userTier} userRank={userRank} packages={packages} onPlay={(videoObj) => triggerCheckInRestriction(() => setActiveVideo(videoObj))} onUpgrade={setUpgradeVideo} delay={i*0.03}/>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* ── PAGINATION BAR (Subscriptions / User Management Style) ── */}
          {totalPages > 1 && (
            <motion.div initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }}
              style={{
                ...glass,
                borderRadius: 22,
                padding: "14px 24px",
                marginTop: 28,
                marginBottom: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 12
              }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#666", fontFamily: "'Inter', sans-serif" }}>
                Showing <strong style={{ color: "#1F1F1F" }}>{startIndex + 1}</strong>–<strong style={{ color: "#1F1F1F" }}>{Math.min(startIndex + itemsPerPage, filtered.length)}</strong> of <strong style={{ color: "#1F1F1F" }}>{filtered.length}</strong> videos
              </span>

              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  style={{
                    height: 32,
                    padding: "0 14px",
                    borderRadius: 100,
                    border: "1px solid rgba(239,229,216,0.9)",
                    background: currentPage === 1 ? "rgba(255,255,255,0.4)" : "#fff",
                    color: currentPage === 1 ? "#BBB" : "#444",
                    fontSize: 12,
                    fontWeight: 700,
                    fontFamily: "'Inter', sans-serif",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    transition: "all 0.2s"
                  }}>
                  <FiChevronLeft size={14} /> Prev
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => {
                  const isActive = currentPage === pageNum;
                  return (
                    <button
                      key={pageNum}
                      type="button"
                      onClick={() => setCurrentPage(pageNum)}
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        fontSize: 12,
                        fontWeight: 800,
                        fontFamily: "'Inter', sans-serif",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        border: isActive ? "none" : "1px solid rgba(239,229,216,0.9)",
                        background: isActive
                          ? `linear-gradient(135deg, ${T.orange}, ${T.orange2})`
                          : "#fff",
                        color: isActive ? "#fff" : "#444",
                        boxShadow: isActive ? "0 4px 14px rgba(242,154,12,0.35)" : "none",
                        transform: isActive ? "scale(1.06)" : "scale(1)"
                      }}>
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  type="button"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  style={{
                    height: 32,
                    padding: "0 14px",
                    borderRadius: 100,
                    border: "1px solid rgba(239,229,216,0.9)",
                    background: currentPage === totalPages ? "rgba(255,255,255,0.4)" : "#fff",
                    color: currentPage === totalPages ? "#BBB" : "#444",
                    fontSize: 12,
                    fontWeight: 700,
                    fontFamily: "'Inter', sans-serif",
                    cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    transition: "all 0.2s"
                  }}>
                  Next <FiChevronRight size={14} />
                </button>
              </div>
            </motion.div>
          )}

          {filtered.length===0 && (
            <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }}
              style={{ textAlign:"center", paddingTop:64, paddingBottom:64 }}>
              <div style={{ width:64, height:64, borderRadius:"50%", background:"rgba(242,154,12,0.1)",
                display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px" }}>
                <FiSearch style={{ fontSize:28, color:T.orange }}/>
              </div>
              <p style={{ fontFamily:"'Poppins',sans-serif", fontSize:18, fontWeight:800, color:"#1F1F1F", marginBottom:6 }}>No videos found</p>
              <p style={{ fontSize:14, color:"#AAA" }}>Try a different search or category</p>
            </motion.div>
          )}

        </div>
      </div>

      {/* ── MODALS ── */}
      <AnimatePresence>
        {upgradeVideo && <UpgradeModal video={upgradeVideo} packages={packages} onClose={() => setUpgradeVideo(null)}/>}
        {activeVideo  && <PlayerModal  video={activeVideo}  onVideoUpdate={handleVideoUpdate} onClose={() => setActiveVideo(null)}/>}
      </AnimatePresence>
    </UserDashboardLayout>
  );
}
